import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import { Panel } from '../components/Panel.jsx';
import { Resource, StatusGlyph } from '../components/StateViews.jsx';
import { formatRelative, formatTimestamp } from '../lib/format.js';
import { passwordPolicySummary } from '../lib/password.js';

export default function PasswordChange({ forced = false }) {
  const { user, changePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const loadPolicy = useCallback((signal) => endpoints.passwordPolicy(signal), []);
  const policy = useResource(loadPolicy, []);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  if (forced && !user?.mustChangePassword) return <Navigate to="/" replace />;

  async function handleSubmit(event) {
    event.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError('The new passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await changePassword(form.currentPassword, form.newPassword);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      if (forced) navigate('/', { replace: true });
      else setNotice('Your password was changed and other sessions were signed out.');
    } catch (changeError) {
      setError(changeError.message || 'Could not change your password.');
    } finally {
      setSubmitting(false);
    }
  }

  const content = (
    <>
      {error ? <p className="notice notice--error" role="alert"><StatusGlyph tone="critical" shape="diamond" />{error}</p> : null}
      {notice ? <p className="notice" role="status"><StatusGlyph tone="good" shape="disc" />{notice}</p> : null}
      <Panel
        title={forced ? 'Choose a new password' : 'Change password'}
        subtitle={passwordPolicySummary(policy.data)}
      >
        <form className="password-form" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field__label">Current password</span>
            <input className="field__input" type="password" autoComplete="current-password" required value={form.currentPassword} onChange={(event) => setForm((value) => ({ ...value, currentPassword: event.target.value }))} disabled={submitting} />
          </label>
          <label className="field">
            <span className="field__label">New password</span>
            <input className="field__input" type="password" autoComplete="new-password" required value={form.newPassword} onChange={(event) => setForm((value) => ({ ...value, newPassword: event.target.value }))} disabled={submitting} />
          </label>
          <label className="field">
            <span className="field__label">Confirm new password</span>
            <input className="field__input" type="password" autoComplete="new-password" required value={form.confirmPassword} onChange={(event) => setForm((value) => ({ ...value, confirmPassword: event.target.value }))} disabled={submitting} />
          </label>
          <button type="submit" className="btn btn--primary" disabled={submitting || !form.currentPassword || !form.newPassword || !form.confirmPassword}>{submitting ? 'Changing…' : 'Change password'}</button>
        </form>
      </Panel>
      {!forced ? <AccountDetails onNotice={setNotice} onError={setError} /> : null}
    </>
  );

  if (forced) {
    return (
      <div className="presto-app forced-password">
        <div className="forced-password__card">
          <div className="forced-password__head">
            <img src="/presto-logo.svg" alt="Presto Studio" />
            <button type="button" className="btn btn--quiet" onClick={signOut}>Sign out</button>
          </div>
          <p className="cap">Password update required</p>
          <h1>Secure your account</h1>
          <p>Your administrator or the Studio password policy requires a new password before you continue.</p>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="page account-page">
      <PageHeader eyebrow="Your account" title={user?.username || 'Account'} lede="Manage your password and signed-in devices." />
      {content}
    </div>
  );
}

function AccountDetails({ onNotice, onError }) {
  const loadSessions = useCallback((signal) => endpoints.ownSessions(signal), []);
  const loadActivity = useCallback((signal) => endpoints.ownActivity(signal), []);
  const sessions = useResource(loadSessions, []);
  const activity = useResource(loadActivity, []);
  const [pending, setPending] = useState(null);

  async function revokeOthers() {
    setPending('others');
    onError(null);
    try {
      const result = await endpoints.revokeOtherSessions();
      onNotice(`${result.revoked} other session${result.revoked === 1 ? '' : 's'} signed out.`);
      sessions.refresh();
      activity.refresh();
    } catch (error) {
      onError(error.message || 'Could not sign out other sessions.');
    } finally {
      setPending(null);
    }
  }

  async function revokeOne(session) {
    setPending(session.id);
    onError(null);
    try {
      await endpoints.revokeOwnSession(session.id);
      onNotice(`${sessionName(session.userAgent)} was signed out.`);
      sessions.refresh();
      activity.refresh();
    } catch (error) {
      onError(error.message || 'Could not sign out that session.');
    } finally {
      setPending(null);
    }
  }

  const otherCount = Array.isArray(sessions.data)
    ? sessions.data.filter((session) => !session.current).length
    : 0;

  return (
    <div className="account-security-grid">
      <Panel
        title="Active sessions"
        subtitle="Devices currently signed in to your Studio account"
        actions={<button type="button" className="btn btn--quiet" onClick={revokeOthers} disabled={pending || otherCount === 0}>{pending === 'others' ? 'Signing out…' : 'Sign out other sessions'}</button>}
      >
        <Resource state={sessions} loadingLabel="Loading your sessions…" loadingRows={2} errorTitle="Could not load your sessions">
          {(items) => (
            <ul className="session-list">
              {items.map((session) => (
                <li key={session.id}>
                  <span className="session-device" aria-hidden="true">{sessionIcon(session.userAgent)}</span>
                  <span className="session-copy">
                    <span className="session-title"><strong>{sessionName(session.userAgent)}</strong>{session.current ? <span className="session-current">Current session</span> : null}</span>
                    <small>{session.ipAddress || 'IP not recorded'} · Last active {formatRelative(session.lastSeenAt)}</small>
                    <small>Started {formatTimestamp(session.createdAt)} · Expires {formatTimestamp(session.expiresAt)}</small>
                  </span>
                  {!session.current ? <button type="button" className="btn btn--quiet" disabled={pending} onClick={() => revokeOne(session)}>{pending === session.id ? 'Signing out…' : 'Sign out'}</button> : null}
                </li>
              ))}
            </ul>
          )}
        </Resource>
      </Panel>

      <Panel title="Recent activity" subtitle="Sign-ins and security changes on your account">
        <Resource state={activity} loadingLabel="Loading account activity…" loadingRows={3} errorTitle="Could not load account activity" isEmpty={(items) => !items?.length} emptyTitle="No recent activity" emptyBody="New sign-ins and account changes will appear here.">
          {(items) => (
            <ol className="account-activity account-activity--self">
              {items.map((event, index) => (
                <li key={`${event.createdAt}-${index}`}>
                  <span><strong>{eventLabel(event.action)}</strong><small>{activityDetail(event)}</small></span>
                  <time dateTime={event.createdAt} title={formatTimestamp(event.createdAt)}>{formatRelative(event.createdAt)}</time>
                </li>
              ))}
            </ol>
          )}
        </Resource>
      </Panel>
    </div>
  );
}

function sessionName(userAgent = '') {
  const browser = /Edg\//.test(userAgent) ? 'Edge' : /Firefox\//.test(userAgent) ? 'Firefox' : /Chrome\//.test(userAgent) ? 'Chrome' : /Safari\//.test(userAgent) ? 'Safari' : 'Studio';
  const device = /iPhone|iPad/.test(userAgent) ? 'iOS' : /Android/.test(userAgent) ? 'Android' : /Macintosh/.test(userAgent) ? 'Mac' : /Windows/.test(userAgent) ? 'Windows' : /Linux/.test(userAgent) ? 'Linux' : 'device';
  return `${browser} on ${device}`;
}

function sessionIcon(userAgent = '') {
  if (/iPhone|Android/.test(userAgent)) return '▯';
  return '▱';
}

function eventLabel(action) {
  return ({ signed_in: 'Signed in', signed_out: 'Signed out', password_changed: 'Password changed', password_reset: 'Password reset', session_revoked: 'Session signed out', sessions_revoked: 'Sessions signed out', other_sessions_revoked: 'Other sessions signed out', role_changed: 'Role changed', user_enabled: 'Account enabled', user_disabled: 'Account disabled', user_created: 'Account created' })[action] || action.replaceAll('_', ' ');
}

function activityDetail(event) {
  if (event.action === 'signed_in' && event.detail) return `From ${event.detail}`;
  if (event.detail) return event.actor ? `${event.detail} · by ${event.actor}` : event.detail;
  return 'Your account';
}
